<template>
    <div class="history-row ds-exchange-history-row">
        <div class="icon-column ds-exchange-history-icon-column">
            <div class="icon-circle ds-exchange-history-icon rounded-full">
            <template v-if="currentIcon">
                <Icon :width="currentIcon.size" :height="currentIcon.size" :vbSize="currentIcon.vbSize">
                    <component :is="currentIcon.component" />
                </Icon>
            </template>
        </div>
        </div>
        <div class="content-column ds-exchange-history-content flex flex-col gap-1 min-w-0">
            <div class="title-row ds-exchange-history-title">
                <span class="font-semibold">{{ actionLabel }}</span>
                <span class="text-gray">{{ date }} {{ time }}</span>
            </div>
            <div class="ds-exchange-history-message">{{ record.message }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Icon from '@/ui/Icon.vue';
import IconCheckin from '@/ui/icons/IconCheckin.vue';
import IconCheckout from '@/ui/icons/IconCheckout.vue';
import IconApprove from '@/ui/icons/IconApprove.vue';
import IconClose from '@/ui/icons/IconClose.vue';
import IconAdd from '@/ui/icons/IconAdd.vue';
import IconResponse from '@/ui/icons/IconResponse.vue';

dayjs.extend(utc);

interface RecordUser {
    id: string
    first_name: string
    middle_name: string | null
    last_name: string
    avatar: string
}

interface HistoryRecord {
    id: string
    action: string
    message: string
    user: RecordUser
    created_at: string
}

interface Props {
    record: HistoryRecord
}

const { record } = defineProps<Props>()

const action = computed(() => record.action)

const actionLabels: Record<string, string> = {
    checkin: 'Чек-ин',
    checkout: 'Чек-аут',
    confirmed: 'Утверждён',
    declined: 'Отклонён',
    response: 'Отклик',
    created: 'Создана',
}

const iconMap: Record<string, { component: object; size: number; vbSize?: number }> = {
    checkin:   { component: IconCheckin,  size: 16, vbSize: 20 },
    checkout:  { component: IconCheckout, size: 16, vbSize: 20 },
    confirmed: { component: IconApprove,  size: 16, vbSize: 24 },
    declined:  { component: IconClose,    size: 16, vbSize: 24 },
    created:   { component: IconAdd,     size: 14, vbSize: 14 },
    response:  { component: IconResponse, size: 16, vbSize: 24 },
}

const actionLabel = computed(() => actionLabels[action.value] ?? action.value)
const currentIcon = computed(() => iconMap[action.value] ?? null)

const parsed = computed(() => dayjs.utc(record.created_at).local())
const date = computed(() => parsed.value.format('DD.MM.YYYY'))
const time = computed(() => parsed.value.format('HH:mm'))
</script>

<style scoped>
.icon-circle {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #606060;
    background-color: #f0f0f0;
}

.history-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.icon-column {
    width: 32px;
    min-width: 32px;
    flex: 0 0 32px;
    padding-top: 2px;
}

.content-column {
    flex: 1;
    min-width: 0;
}

.title-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
}
</style>
